import { Observation } from "@medplum/fhirtypes";

import {
  ClientPostpartumFlowsheetForms,
  NewbornPostpartumFlowsheetForms,
} from "@/encounters/components/EncounterLabor/constants/postpartum-flowsheets";
import { PostpartumRow } from "@/encounters/components/EncounterLabor/types/postpartum";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { QuiltedHealthCodes } from "@/fhir/enums/quilted-health";
import { getLocalDateString, localTimeFromISO } from "@/utils/datetime";
import {
  getQuestionnaireResponseIdFromObservationDerivedFrom,
  joinReferenceString,
} from "@/utils/medplum";
import { isFlowsheetObservationVoidStatus } from "@/utils/observation-flowsheet-status";
import {
  compareObservationsByVersionIdThenRecencyThenIdDesc,
  pickObservationNewerByRecencyThenId,
} from "@/utils/observation-recency";

/**
 * Builds a stable key for postpartum flowsheet rows: same encounter + subject + primary form code
 * + effective time should only surface one current `final` Observation (supersede/bot races).
 */
function normalizeEffectiveDateTimeForDedupeKey(effectiveDateTime: string): string {
  const parsedMillis = Date.parse(effectiveDateTime);
  if (!Number.isFinite(parsedMillis)) {
    return effectiveDateTime;
  }
  // Truncate sub-second noise so bot races for the same charted time still dedupe.
  return new Date(Math.floor(parsedMillis / 1000) * 1000).toISOString();
}

function getPostpartumObservationDedupeKey(observation: Observation): string | undefined {
  const subject = observation.subject?.reference;
  const encounter = observation.encounter?.reference;
  const code = observation.code?.coding?.[0]?.code;
  const effective = observation.effectiveDateTime;
  if (!subject || !encounter || !code || !effective) {
    return undefined;
  }
  return `${subject}|${encounter}|${code}|${normalizeEffectiveDateTimeForDedupeKey(effective)}`;
}

function pickNewestFinalPostpartumObservation(candidates: Observation[]): Observation {
  return [...candidates].sort(compareObservationsByVersionIdThenRecencyThenIdDesc)[0];
}

/**
 * When multiple `final` Observations share subject, encounter, flowsheet code, and effective time
 * (e.g. supersede did not cancel a legacy row missing `derivedFrom`), keep one winner for display.
 */
export function dedupeConcurrentFinalPostpartumObservations(
  observations: Observation[],
): Observation[] {
  const finals = observations.filter((observation) => observation.status === "final");
  if (finals.length <= 1) {
    return observations;
  }

  const byKey = new Map<string, Observation[]>();
  const finalsWithoutDedupeKey = new Set<string>();

  for (const observation of finals) {
    const key = getPostpartumObservationDedupeKey(observation);
    if (!key) {
      if (observation.id) {
        finalsWithoutDedupeKey.add(observation.id);
      }
      continue;
    }
    const bucket = byKey.get(key) ?? [];
    bucket.push(observation);
    byKey.set(key, bucket);
  }

  const keepFinalIds = new Set<string>();
  for (const observationId of finalsWithoutDedupeKey) {
    keepFinalIds.add(observationId);
  }

  for (const bucket of byKey.values()) {
    if (bucket.length === 1) {
      const id = bucket[0].id;
      if (id) {
        keepFinalIds.add(id);
      }
      continue;
    }
    const winner = pickNewestFinalPostpartumObservation(bucket);
    if (winner.id) {
      keepFinalIds.add(winner.id);
    }
  }

  return observations.filter((observation) => {
    if (observation.status !== "final") {
      return true;
    }
    const id = observation.id;
    if (!id) {
      return true;
    }
    if (finalsWithoutDedupeKey.has(id)) {
      return true;
    }
    const key = getPostpartumObservationDedupeKey(observation);
    if (!key) {
      return true;
    }
    return keepFinalIds.has(id);
  });
}

export async function getPostpartumRows(
  flowsheetForms: Record<string, string>,
  observations: Observation[],
): Promise<Record<string, PostpartumRow[]>> {
  // Filter observations by form codes and status first
  const validCodes = new Set(Object.values(flowsheetForms));

  // Also include shared forms (Cord Cut/Clamped and First Latch) from the other flowsheet
  const sharedFormCodes: string[] = [
    ClientPostpartumFlowsheetForms.ClientCordCutClamped,
    ClientPostpartumFlowsheetForms.ClientFirstLatch,
    NewbornPostpartumFlowsheetForms.NewbornCordCutClamped,
    NewbornPostpartumFlowsheetForms.NewbornFirstLatch,
  ];

  const filteredObservations = observations.filter((obs) => {
    if (isFlowsheetObservationVoidStatus(obs.status)) {
      return false;
    }

    const code = obs.code?.coding?.[0]?.code;

    // Include observations that match the flowsheet form codes
    if (validCodes.has(code ?? "")) {
      return true;
    }

    // Include shared forms (Cord Cut/Clamped and First Latch) from either flowsheet
    if (code && sharedFormCodes.includes(code)) {
      return true;
    }

    // Include observations with LOINC codes that have row components (like CumulativeBloodLoss, BirthDateTime)
    if (code === LOINCCodes.CumulativeBloodLoss || code === QuiltedHealthCodes.BirthDateTime) {
      return true;
    }

    return false;
  });

  const dedupedObservations = dedupeObservationsByLatestQuestionnaireResponseAndCode(
    dedupeConcurrentFinalPostpartumObservations(filteredObservations),
  );

  // Process observations into rows by date and time
  const rowsByDate = dedupedObservations.reduce<Record<string, Record<string, Observation[]>>>(
    (acc, observation) => {
      if (!observation.effectiveDateTime) return acc;

      // Convert datetime to local date for proper grouping
      const date = getLocalDateString(observation.effectiveDateTime);
      const time = localTimeFromISO(observation.effectiveDateTime);

      if (!acc[date]) {
        acc[date] = {};
      }
      if (!acc[date][time]) {
        acc[date][time] = [];
      }

      acc[date][time].push(observation);
      return acc;
    },
    {},
  );

  // Sort dates in descending order
  const sortedDates = Object.keys(rowsByDate).sort((a, b) => b.localeCompare(a));

  // Sort times within each date in descending order
  const result: Record<string, PostpartumRow[]> = {};
  for (const date of sortedDates) {
    const times = Object.keys(rowsByDate[date]).sort((a, b) => b.localeCompare(a));
    result[date] = times
      .map((time) => ({
        time,
        observations: rowsByDate[date][time],
      }))
      .filter((row) => row.observations && row.observations.length > 0); // Filter out empty groups
  }

  // Filter out dates with no groups (all groups empty)
  Object.keys(result).forEach((date) => {
    if (result[date].length === 0) {
      delete result[date];
    }
  });

  return result;
}

export function filterObservationsByForms(
  observations: Observation[],
  formCodes: Record<string, string>,
): Observation[] {
  const validCodes = new Set(Object.values(formCodes));
  const filtered = observations.filter((obs) => {
    if (isFlowsheetObservationVoidStatus(obs.status)) {
      return false;
    }

    return obs.code?.coding?.some((coding) => validCodes.has(coding?.code ?? ""));
  });
  return dedupeObservationsByLatestQuestionnaireResponseAndCode(filtered);
}

/**
 * Returns a canonical `QuestionnaireResponse/{id}` reference from `derivedFrom`, or undefined.
 */
function getQuestionnaireResponseDedupeReference(observation: Observation): string | undefined {
  const questionnaireResponseId = getQuestionnaireResponseIdFromObservationDerivedFrom(
    observation.derivedFrom,
  );
  return questionnaireResponseId
    ? joinReferenceString("QuestionnaireResponse", questionnaireResponseId)
    : undefined;
}

/**
 * When two `final` Observations share the same primary `code` and `derivedFrom` QuestionnaireResponse
 * (e.g. cancel-after-supersede failed server-side), keep only the newest for display. Output order
 * follows the input list: each dedupe group is emitted at the earliest input index in that group.
 */
function dedupeObservationsByLatestQuestionnaireResponseAndCode(
  observations: Observation[],
): Observation[] {
  interface DedupeGroupState {
    winner: Observation;
    minIndex: number;
  }

  const dedupeKeyToState = new Map<string, DedupeGroupState>();
  const withoutDedupeKey: { observation: Observation; index: number }[] = [];

  observations.forEach((observation, index) => {
    const code = observation.code?.coding?.[0]?.code;
    const questionnaireResponseReference = getQuestionnaireResponseDedupeReference(observation);

    if (!code || !questionnaireResponseReference) {
      withoutDedupeKey.push({ observation, index });
      return;
    }

    const mapKey = `${code}|${questionnaireResponseReference}`;
    const existing = dedupeKeyToState.get(mapKey);
    if (!existing) {
      dedupeKeyToState.set(mapKey, { winner: observation, minIndex: index });
      return;
    }

    const nextWinner = pickObservationNewerByRecencyThenId(existing.winner, observation);

    dedupeKeyToState.set(mapKey, {
      winner: nextWinner,
      minIndex: Math.min(existing.minIndex, index),
    });
  });

  const keyedOutputs = [...dedupeKeyToState.values()].map((state) => ({
    observation: state.winner,
    index: state.minIndex,
  }));

  return [...keyedOutputs, ...withoutDedupeKey]
    .sort((first, second) => first.index - second.index)
    .map((entry) => entry.observation);
}
